/** The draft's changes as the operator's PUT body (backend P2): the WHOLE form, every key
 *  required — the stored values with the draft's changes over them.
 *
 *  - `settings`: the 13 keys;
 *  - `fields.lead` / `fields.party`: the lists, a row a definition names carrying the
 *    definition's label and type (as the server stores it), each row reduced to the
 *    spec's keys;
 *  - `waivers`: `[{waiver_id}]`;
 *  - `definitions`: the stored custom fields as the draft leaves them (a removed one left
 *    out); a new custom field is not listed — the server creates it from its row;
 *  - `version`: the baseline's; `reason`: the ledger's description, trimmed. */
import type { OperatorPreCheckField, OperatorPreCheckValues, OperatorPreCheckWrite } from '@/api/operatorPrecheck';
import { PRECHECK, type PreCheckField } from '@/features/precheck/model';
import type { DraftWireChange } from '@/features/records/draft/changeSet';

import type { OperatorPreCheckBaseline } from './baselines';
import { asDefinitions, asWaiverRows, DEFINITIONS_PATH, LEAD_FIELDS, overlayDefinitions, PARTY_FIELDS, SETTINGS_KEYS, WAIVERS_PATH } from './model';

/** The value of every path once these changes are applied to the baseline. */
export function valuesAfter(changes: readonly DraftWireChange[], base: OperatorPreCheckBaseline): (path: string) => unknown {
  const stored = base.paths[base.rec] ?? {};
  const touched = new Map<string, unknown>();
  for (const c of changes) if (`${c.entity}:${c.id}` === base.rec && c.field) touched.set(c.field, c.to);
  return (path) => (touched.has(path) ? touched.get(path) : stored[path]);
}

function wireRow(f: PreCheckField): OperatorPreCheckField {
  return {
    id: f.id,
    name: String(f.name ?? '').trim(),
    type: f.type,
    ...(typeof f.required === 'boolean' ? { required: f.required } : {}),
    ...(typeof f.multiselect === 'boolean' ? { multiselect: f.multiselect } : {}),
    ...(typeof f.category_id === 'string' ? { category_id: f.category_id } : {}),
  };
}

export function compileOperatorPreCheck(
  changes: readonly DraftWireChange[],
  base: OperatorPreCheckBaseline,
  reason: string,
): OperatorPreCheckWrite {
  const v = valuesAfter(changes, base);
  const defs = asDefinitions(v(DEFINITIONS_PATH));
  const settings = Object.fromEntries(SETTINGS_KEYS.map((k) => [k, v(PRECHECK(k))])) as unknown as OperatorPreCheckValues;
  return {
    settings,
    fields: {
      lead: overlayDefinitions(v(LEAD_FIELDS), defs).map(wireRow),
      party: overlayDefinitions(v(PARTY_FIELDS), defs).map(wireRow),
    },
    waivers: asWaiverRows(v(WAIVERS_PATH)).map((w) => ({ waiver_id: w.waiver_id })),
    definitions: defs.map((d) => ({ id: d.id, name: d.name.trim(), type: d.type })),
    version: base.version,
    reason: reason.trim(),
  };
}

/** The settings object a guest form is drawn from (the preview): the settings, the lists
 *  as guests get them, the waivers — after these changes. */
export function settingsAfter(valueOf: (path: string) => unknown): Record<string, unknown> {
  const defs = asDefinitions(valueOf(DEFINITIONS_PATH));
  const out: Record<string, unknown> = Object.fromEntries(SETTINGS_KEYS.map((k) => [k, valueOf(PRECHECK(k))]));
  out.lead_booker_fields = overlayDefinitions(valueOf(LEAD_FIELDS), defs);
  out.party_member_fields = overlayDefinitions(valueOf(PARTY_FIELDS), defs);
  out.waivers = asWaiverRows(valueOf(WAIVERS_PATH));
  return out;
}
